import React, { useEffect, useRef } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import {
  controlLabel,
  flushUsage,
  screenOf,
  setUsageConsent,
  setUsageSite,
  track,
} from '../../lib/usage';
import { carriedConsent } from '../../lib/sharedConsent';
import { showInkToast } from '../cms/InkToast';

export const USAGE_CONSENT = gql`
  query UsageConsent {
    usageConsent {
      asked
      consent
      available
    }
  }
`;

export const SET_USAGE_CONSENT = gql`
  mutation SetUsageConsent($consent: Boolean!, $source: String) {
    setUsageConsent(consent: $consent, source: $source) {
      asked
      consent
      available
    }
  }
`;

type Consent = { asked: boolean; consent: boolean | null; available: boolean };

/** The person's answer, shared by the prompt, Profile and tracking. */
export function useUsageConsent() {
  const { data, loading } = useQuery<{ usageConsent: Consent }>(USAGE_CONSENT, {
    fetchPolicy: 'cache-first',
  });
  const [save, { loading: saving }] = useMutation<{ setUsageConsent: Consent }>(SET_USAGE_CONSENT, {
    update(cache, { data: res }) {
      if (res)
        cache.writeQuery({ query: USAGE_CONSENT, data: { usageConsent: res.setUsageConsent } });
    },
  });
  return {
    consent: data?.usageConsent ?? null,
    loading,
    saving,
    /** source: app (the ask) | profile | site_banner_v<N> (carried from www.tinycms.ai). */
    answer: (on: boolean, source = 'app') => save({ variables: { consent: on, source } }),
  };
}

/**
 * In the signed-in shell (AppShell): screens and the app's own button and tab
 * clicks, only once the person has agreed (lib/usage.ts), and the one-time ask.
 */
export function UsageTracking() {
  const { consent, saving, answer } = useUsageConsent();
  const { accountId } = useAccount();
  const location = useLocation();
  const on = consent?.consent === true;

  useEffect(() => {
    setUsageConsent(on);
  }, [on]);
  useEffect(() => {
    setUsageSite(accountId || null);
  }, [accountId]);
  useEffect(() => {
    if (on) track('Screen viewed', { screen: screenOf(location.pathname) });
  }, [on, location.pathname]);
  useEffect(() => {
    if (!on) return;
    const onClick = (e: MouseEvent) => {
      const hit = controlLabel(e.target as Element | null);
      if (hit) track(hit.type, { label: hit.label, screen: screenOf(window.location.pathname) });
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushUsage();
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flushUsage);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flushUsage);
    };
  }, [on]);

  // Answered on www.tinycms.ai's cookie banner, which names the app too: that
  // answer is recorded instead of asking again (lib/sharedConsent.ts).
  const carried =
    consent && consent.available && !consent.asked ? carriedConsent(document.cookie) : null;
  const carrying = useRef(false);
  useEffect(() => {
    if (!carried || carrying.current) return;
    carrying.current = true;
    void answer(carried.consent, carried.source);
  }, [carried, answer]);

  // Asked once, never before it can be saved (the setting's migration).
  if (!consent || !consent.available || consent.asked || carried) return null;
  return (
    <div className="ink-usage-ask" role="dialog" aria-labelledby="ink-usage-ask-title">
      <div className="ink-usage-ask-title" id="ink-usage-ask-title">
        Help us improve tinycms
      </div>
      <p className="ink-usage-ask-text">
        May we collect information about how you use the app, so we can make it better? You can
        change this any time in Profile.{' '}
        <a href="https://www.tinycms.ai/privacy#10-cookies" target="_blank" rel="noreferrer">
          Privacy
        </a>
      </p>
      <div className="ink-usage-ask-actions">
        <button
          type="button"
          className="ink-btn ink-btn-quiet"
          disabled={saving}
          onClick={() => void answer(false)}
        >
          No thanks
        </button>
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={saving}
          onClick={() => void answer(true)}
        >
          Share usage
        </button>
      </div>
    </div>
  );
}

/** Profile → Privacy: the same answer, changed any time. */
/**
 * Profile → Privacy (Prototype: Profile privacy, backlog 066ae462): the one
 * switch, what is and isn't shared (usageAnalytics.js sends screens and
 * button labels, never what anyone writes or readers' data), and the policy.
 * The row toggles as the design's does; the link sits outside the switch, so
 * opening it never flips the answer. Off until the person says yes.
 */
export function UsageConsentSetting() {
  const { consent, saving, answer } = useUsageConsent();
  if (!consent?.available) return null;
  const on = consent.consent === true;
  const toggle = async () => {
    if (saving) return;
    const res = await answer(!on, 'profile');
    if (res?.data) showInkToast(on ? 'Usage sharing off.' : 'Usage sharing on. Thanks.');
  };
  return (
    <div className="ink-profile-notif" onClick={() => void toggle()}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="ink-profile-notif-name">Share how you use the app</span>
        <span className="ink-profile-notif-desc">
          Information about how you use the app, so we can make tinycms better. Never your content
          or your readers&rsquo; data.{' '}
          <a
            href="https://www.tinycms.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="ink-profile-notif-link"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy policy
          </a>
        </span>
      </span>
      <button
        type="button"
        className="ink-profile-switch"
        role="switch"
        aria-checked={on}
        aria-label="Share how you use the app"
        data-on={on ? 'true' : 'false'}
        disabled={saving}
        onClick={(e) => {
          e.stopPropagation();
          void toggle();
        }}
      />
    </div>
  );
}

export default React.memo(UsageTracking);
